import { useChat } from "@ai-sdk/react";
import type {
  Asset,
  ChatToolOutput,
  DocumentPages,
  Job,
  LookAtImagesOutput,
  ReadDocumentOutput,
  ReadWebpageOutput,
} from "@studio/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { DefaultChatTransport, type UIMessage } from "ai";
import { ArrowUp, BookOpen, ChevronRight, FileText, Globe, Image as ImageIcon, Paperclip, Plus, Square, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { documentLength, MediaThumb } from "@/components/MediaThumb";
import { RunCard } from "@/components/RunCard";
import { Button, IconButton, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { useLive } from "@/lib/events";
import { cn, formatClock, formatWait } from "@/lib/format";

const STARTERS = [
  "Make a 5-second birthday video: balloons rising over a sunny garden party",
  "Write a cheerful song for my friend Anna who loves hiking",
  "Draw a cosy winter cabin, then turn it into a short video with falling snow",
];

/** Phone: the list of chats. Desktop: an empty conversation (the list lives in the sidebar). */
export function ChatsIndexPage() {
  const chats = useQuery({ queryKey: ["chats"], queryFn: api.chats });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const start = async (text?: string) => {
    const thread = await api.createChat();
    queryClient.invalidateQueries({ queryKey: ["chats"] });
    navigate({ to: "/chat/$chatId", params: { chatId: thread.id }, search: text ? { first: text } : undefined } as never);
  };
  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Chat" />
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[760px] px-4 md:px-6">
          <div className="md:hidden">
            <Button variant="primary" className="mt-4 w-full" onClick={() => start()}>
              <Plus className="size-4" aria-hidden />
              New chat
            </Button>
            <ul className="mt-4 flex flex-col">
              {(chats.data ?? []).map((thread) => (
                <li key={thread.id}>
                  <Link
                    to="/chat/$chatId"
                    params={{ chatId: thread.id }}
                    className="flex min-h-14 items-center gap-3 border-b border-line/50 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px]">{thread.title || "New chat"}</p>
                      <p className="text-[13px] text-mute">{formatClock(thread.updatedAt)}</p>
                    </div>
                    <ChevronRight className="size-4 text-mute" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <section className="hidden min-h-[56dvh] flex-col justify-end pb-4 md:flex">
            <h2 className="font-display text-[32px] leading-tight">Ask for anything</h2>
            <p className="mt-2 max-w-[56ch] text-mute">
              Talk it through, attach photos or PDFs, and ask for pictures, videos or songs — they appear right in the conversation.
            </p>
            <div className="mt-5 flex flex-col gap-2">
              {STARTERS.map((line) => (
                <button
                  key={line}
                  type="button"
                  onClick={() => start(line)}
                  className="rounded-[var(--radius-control)] border border-line/40 px-4 py-3 text-left text-[15px] text-paper/90 hover:border-line hover:bg-panel"
                >
                  {line}
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>
      <div className="hidden md:block">
        <Composer working={false} onStop={() => {}} onSend={(text) => text && start(text)} allowAttach={false} />
      </div>
    </div>
  );
}

export function ChatPage() {
  const { chatId } = useParams({ from: "/chat/$chatId" });
  const thread = useQuery({ queryKey: ["chat", chatId], queryFn: () => api.chat(chatId), staleTime: Number.POSITIVE_INFINITY });
  if (thread.isError) {
    return (
      <div className="flex h-full flex-col items-start justify-center gap-3 px-6">
        <p className="text-lg font-medium">This chat is gone</p>
        <Link to="/chat">
          <Button variant="outline">Back to chats</Button>
        </Link>
      </div>
    );
  }
  if (!thread.data) {
    return (
      <div className="flex h-full items-center justify-center text-mute">
        <Spinner />
      </div>
    );
  }
  return <Conversation key={chatId} chatId={chatId} title={thread.data.thread.title} initial={thread.data.messages} />;
}

interface StatusData {
  state: "waiting_gpu" | "loading_model" | "ready";
  reason: string;
  etaSec: number | null;
}

function Conversation({ chatId, title, initial }: { chatId: string; title: string; initial: UIMessage[] }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusData | null>(null);
  const chat = useChat({
    id: chatId,
    messages: initial,
    transport: new DefaultChatTransport({ api: `/api/chats/${chatId}/messages` }),
    onData: (part) => {
      if (part.type === "data-status") setStatus(part.data as StatusData);
    },
    onFinish: () => {
      setStatus(null);
      queryClient.invalidateQueries({ queryKey: ["chats"] });
    },
    onError: (error) => {
      setStatus(null);
      toast.error(error.message || "The assistant could not answer. Try again in a moment.");
    },
  });
  const scroller = useRef<HTMLDivElement>(null);
  const working = chat.status === "submitted" || chat.status === "streaming";

  // Starter text passed from the chats page.
  const firstSent = useRef(false);
  useEffect(() => {
    const first = new URLSearchParams(window.location.search).get("first");
    if (first && !firstSent.current && initial.length === 0) {
      firstSent.current = true;
      void chat.sendMessage({ text: first });
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [chat, initial.length]);

  // The server names the chat after its first message; follow the list so the header catches up.
  const chats = useQuery({ queryKey: ["chats"], queryFn: () => api.chats() });
  const heading = chats.data?.find((c) => c.id === chatId)?.title || title || "New chat";

  // Stay pinned to the newest message while it streams or a result grows in, unless the person scrolled up to read.
  const content = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  useEffect(() => {
    const el = scroller.current;
    const inner = content.current;
    if (!el || !inner) return;
    el.scrollTo({ top: el.scrollHeight });
    const onScroll = () => {
      pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    };
    const follow = new ResizeObserver(() => {
      if (pinned.current) el.scrollTo({ top: el.scrollHeight });
    });
    el.addEventListener("scroll", onScroll, { passive: true });
    follow.observe(inner);
    return () => {
      el.removeEventListener("scroll", onScroll);
      follow.disconnect();
    };
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new message of your own always brings you down
  useEffect(() => {
    const last = chat.messages.at(-1);
    if (last?.role === "user") pinned.current = true;
  }, [chat.messages.length]);

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={heading} />
      <div ref={scroller} className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        <div ref={content} className="mx-auto flex w-full max-w-[760px] flex-col gap-6 px-4 py-4 md:px-6">
          {chat.messages.length === 0 ? (
            <div className="flex min-h-[40dvh] flex-col justify-end gap-2">
              <h2 className="font-display text-[26px]">What are we making?</h2>
              {STARTERS.map((line) => (
                <button
                  key={line}
                  type="button"
                  onClick={() => chat.sendMessage({ text: line })}
                  className="rounded-[var(--radius-control)] border border-line/40 px-4 py-3 text-left text-[15px] text-paper/90 hover:border-line hover:bg-panel"
                >
                  {line}
                </button>
              ))}
            </div>
          ) : null}
          {chat.messages.map((message) => (
            <Message key={message.id} message={message} />
          ))}
          {working && status && status.state !== "ready" ? (
            <p className="flex items-center gap-2 text-sm text-mute">
              <Spinner className="text-signal" />
              {status.reason}
              {status.etaSec ? ` · about ${formatWait(status.etaSec)}` : ""}
            </p>
          ) : working && chat.status === "submitted" ? (
            <p className="flex items-center gap-2 text-sm text-mute">
              <Spinner className="text-signal" />
              Thinking…
            </p>
          ) : null}
        </div>
      </div>
      <Composer
        working={working}
        onStop={chat.stop}
        onSend={(text, files) => chat.sendMessage({ text, files })}
      />
    </div>
  );
}

type FilePart = { type: "file"; mediaType: string; url: string; filename?: string };

function Message({ message }: { message: UIMessage }) {
  if (message.role === "user") {
    const files = message.parts.filter((p): p is FilePart => p.type === "file");
    const text = message.parts
      .filter((p) => p.type === "text")
      .map((p) => (p as { text: string }).text)
      .join("\n");
    return (
      <div className="flex flex-col items-end gap-2">
        {files.length ? (
          <div className="flex flex-wrap justify-end gap-2">
            {files.map((file) => (
              <AttachedFile key={file.url} part={file} />
            ))}
          </div>
        ) : null}
        {text ? (
          <p className="max-w-[85%] whitespace-pre-wrap rounded-[18px] rounded-br-[6px] bg-raised px-4 py-2.5 text-[15px] leading-6">
            {text}
          </p>
        ) : null}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {message.parts.map((part, index) => {
        const key = `${message.id}-${index}`;
        if (part.type === "text") {
          return (
            <div key={key} className="chat-prose max-w-none text-[15px] leading-7">
              <Markdown remarkPlugins={[remarkGfm]}>{part.text}</Markdown>
            </div>
          );
        }
        if (part.type === "reasoning") {
          return <Reasoning key={key} text={(part as { text: string }).text} />;
        }
        if (part.type.startsWith("tool-")) {
          return <ToolCall key={key} part={part as unknown as ToolPart} />;
        }
        return null;
      })}
    </div>
  );
}

function AttachedFile({ part }: { part: FilePart }) {
  const id = part.url.match(/\/api\/assets\/([^/]+)\/file/)?.[1];
  const asset = useQuery({ queryKey: ["asset", id], queryFn: () => api.asset(id as string), enabled: !!id });
  if (!asset.data) return <div className="size-24 animate-pulse rounded-[var(--radius-media)] bg-panel" />;
  return (
    <Link to="." search={(prev: Record<string, unknown>) => ({ ...prev, asset: asset.data?.id })}>
      {asset.data.kind === "document" ? (
        <DocumentChip asset={asset.data} />
      ) : (
        <MediaThumb asset={asset.data} className="size-24" edge={asset.data.kind !== "image"} />
      )}
    </Link>
  );
}

/** A PDF in a message: its first page, name and length. */
function DocumentChip({ asset, className }: { asset: Asset; className?: string }) {
  return (
    <span
      className={cn(
        "flex w-[240px] max-w-full items-center gap-3 rounded-[var(--radius-control)] border border-line bg-panel p-1.5 pr-3 text-left",
        className,
      )}
    >
      <MediaThumb asset={asset} className="h-14 w-11 shrink-0" edge={false} />
      <span className="min-w-0">
        <span className="block truncate text-[14px] leading-5">{asset.name ?? "Document"}</span>
        <span className="block text-[12px] leading-5 text-mute">PDF, {documentLength(asset)}</span>
      </span>
    </span>
  );
}

function Reasoning({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  if (!text.trim()) return null;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-[13px] font-medium text-mute hover:text-paper"
        aria-expanded={open}
      >
        <ChevronRight className={cn("size-4 transition-transform", open && "rotate-90")} aria-hidden />
        Thought it through
      </button>
      {open ? <p className="mt-2 whitespace-pre-wrap border-l-2 border-line pl-3 text-[13px] leading-6 text-mute">{text}</p> : null}
    </div>
  );
}

interface ToolPart {
  type: string;
  state: "input-streaming" | "input-available" | "output-available" | "output-error";
  input?: Record<string, unknown>;
  output?: ChatToolOutput;
  errorText?: string;
}

/** "pages 3-7 of 40", "page 2 of 5". */
function pagesOf(doc: DocumentPages): string {
  const several = /[-,]/.test(doc.pages);
  return `${several ? "pages" : "page"} ${doc.pages.replace(/-/g, "–")} of ${doc.pageCount}`;
}

function DocumentRead({ part }: { part: ToolPart }) {
  const input = part.input as { assetId?: string; pages?: string } | undefined;
  const read = part.output as unknown as ReadDocumentOutput | undefined;
  const asset = useQuery({
    queryKey: ["asset", input?.assetId],
    queryFn: () => api.asset(input?.assetId as string),
    enabled: !!input?.assetId && !read,
    retry: false,
  });
  const name = read?.name ?? read?.title ?? asset.data?.name ?? "the document";
  if (part.state === "output-error") {
    return (
      <p className="flex items-start gap-2 text-[13px] text-mute">
        <FileText className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span>
          Could not read {name}: {part.errorText}
        </span>
      </p>
    );
  }
  const done = part.state === "output-available";
  const where = read ? pagesOf(read) : input?.pages ? `pages ${input.pages}` : "";
  const looked = read?.shownPages.length ? `, looked at ${read.shownPages.length === 1 ? "page" : "pages"} ${read.shownPages.join(", ")}` : "";
  return (
    <p className="flex min-w-0 items-center gap-2 text-[13px] text-mute">
      {done ? <FileText className="size-4 shrink-0" aria-hidden /> : <Spinner className="text-signal" />}
      <span className="min-w-0 truncate">
        {done ? "Read " : "Reading "}
        <span className="text-paper">{name}</span>
        {where ? `, ${where}` : ""}
        {looked}
      </span>
    </p>
  );
}

const hostOf = (address: string) => {
  try {
    return new URL(address).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

function ImagesLook({ part }: { part: ToolPart }) {
  const input = part.input as { urls?: string[] } | undefined;
  const looked = part.output as unknown as LookAtImagesOutput | undefined;
  const asked = input?.urls?.length ?? 0;
  const hosts = [...new Set((input?.urls ?? []).map(hostOf).filter(Boolean))];
  const from = hosts.length === 1 ? ` from ${hosts[0]}` : hosts.length > 1 ? ` from ${hosts.length} sites` : "";
  if (part.state === "output-error") {
    return (
      <p className="flex items-start gap-2 text-[13px] text-mute">
        <ImageIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span>Could not look at the pictures: {part.errorText}</span>
      </p>
    );
  }
  const done = part.state === "output-available";
  const shown = looked?.shownImages.length ?? 0;
  const failed = looked ? looked.images.length - shown : 0;
  const count = (n: number) => `${n} ${n === 1 ? "picture" : "pictures"}`;
  return (
    <p className="flex min-w-0 items-center gap-2 text-[13px] text-mute">
      {done ? <ImageIcon className="size-4 shrink-0" aria-hidden /> : <Spinner className="text-signal" />}
      <span className="min-w-0 truncate">
        {done ? `Looked at ${count(shown)}${from}` : `Looking at ${count(asked)}${from}…`}
        {failed ? ` (${failed} could not be loaded)` : ""}
      </span>
    </p>
  );
}

function ToolCall({ part }: { part: ToolPart }) {
  if (part.type === "tool-read_document") return <DocumentRead part={part} />;
  if (part.type === "tool-look_at_images") return <ImagesLook part={part} />;
  if (part.type === "tool-read_webpage") {
    const input = part.input as { url?: string; offset?: number } | undefined;
    const read = part.output as unknown as ReadWebpageOutput | undefined;
    const address = read?.url ?? input?.url ?? "";
    let host = address;
    try {
      host = new URL(address).hostname.replace(/^www\./, "");
    } catch {
      /* show what the assistant passed */
    }
    const more = read?.document
      ? `, ${pagesOf(read.document)}`
      : read?.shownImages?.length
        ? " (a picture)"
        : input?.offset
          ? " (more)"
          : "";
    if (part.state === "output-error") {
      return (
        <p className="flex items-start gap-2 text-[13px] text-mute">
          <Globe className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            Could not read {host || "the page"}: {part.errorText}
          </span>
        </p>
      );
    }
    return (
      <p className="flex min-w-0 items-center gap-2 text-[13px] text-mute">
        {part.state === "output-available" ? <Globe className="size-4 shrink-0" aria-hidden /> : <Spinner className="text-signal" />}
        <span className="min-w-0 truncate">
          {part.state === "output-available" ? "Read " : "Reading "}
          {address ? (
            <a href={address} target="_blank" rel="noreferrer noopener" className="text-paper underline-offset-2 hover:underline">
              {read?.title ? `${read.title} · ${host}` : host}
            </a>
          ) : (
            "a page"
          )}
          {more}
        </span>
      </p>
    );
  }
  if (part.type === "tool-load_skill") {
    const name = typeof part.input?.name === "string" ? part.input.name : "a";
    if (part.state === "output-error") return null;
    return (
      <p className="flex items-center gap-2 text-[13px] text-mute">
        {part.state === "output-available" ? <BookOpen className="size-4" aria-hidden /> : <Spinner className="text-signal" />}
        Using the {name} skill
      </p>
    );
  }
  const what = part.type === "tool-generate_video" ? "video" : part.type === "tool-generate_music" ? "song" : "picture";
  if (part.state === "output-error") {
    return <p className="rounded-[var(--radius-control)] border border-danger/40 px-4 py-3 text-sm text-danger">{part.errorText}</p>;
  }
  if (part.state !== "output-available" || !part.output?.jobId) {
    return (
      <p className="flex items-center gap-2 text-sm text-mute">
        <Spinner className="text-signal" />
        Preparing the {what}…
      </p>
    );
  }
  return <ChatJob jobId={part.output.jobId} />;
}

function ChatJob({ jobId }: { jobId: string }) {
  const live = useLive((s) => s.jobs[jobId]);
  const fetched = useQuery({ queryKey: ["job", jobId], queryFn: () => api.job(jobId), enabled: !live });
  const upsert = useLive((s) => s.upsertJob);
  useEffect(() => {
    if (fetched.data && !live) upsert(fetched.data);
  }, [fetched.data, live, upsert]);
  const job: Job | undefined = live ?? fetched.data;
  if (!job) return <div className="h-40 animate-pulse rounded-[var(--radius-control)] bg-panel" />;
  return (
    <div className="max-w-[520px] rounded-[var(--radius-control)] border border-line bg-panel/60 px-4 [&_article]:py-4">
      <RunCard job={job} layout="stack" />
    </div>
  );
}

function Composer({
  working,
  onSend,
  onStop,
  allowAttach = true,
}: {
  working: boolean;
  onSend: (text: string, files: FilePart[]) => void;
  onStop: () => void;
  allowAttach?: boolean;
}) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<Asset[]>([]);
  const [uploading, setUploading] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const area = useRef<HTMLTextAreaElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the text changes
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, window.innerHeight * 0.3)}px`;
  }, [text]);

  const send = () => {
    if (working || uploading || (!text.trim() && attachments.length === 0)) return;
    const files: FilePart[] = attachments.map((asset) => ({
      type: "file",
      mediaType: asset.mime,
      url: asset.url,
      filename: asset.id,
    }));
    onSend(text.trim(), files);
    setText("");
    setAttachments([]);
  };

  const attach = async (files: FileList | File[] | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const asset = await api.upload(file);
        setAttachments((prev) => [...prev, asset]);
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className="shrink-0 px-3 pb-3 md:px-6 md:pb-5">
      <div className="mx-auto w-full max-w-[760px] rounded-[var(--radius-dock)] border border-line bg-panel">
        {attachments.length ? (
          <div className="flex gap-2 overflow-x-auto px-3 pt-3">
            {attachments.map((asset) => (
              <div key={asset.id} className="relative shrink-0">
                {asset.kind === "document" ? (
                  <DocumentChip asset={asset} className="h-16 w-[220px]" />
                ) : (
                  <MediaThumb asset={asset} className="size-16" edge={false} />
                )}
                <button
                  type="button"
                  onClick={() => setAttachments((prev) => prev.filter((a) => a.id !== asset.id))}
                  className="absolute -right-1.5 -top-1.5 inline-flex size-6 items-center justify-center rounded-full border border-line bg-raised"
                  aria-label="Remove attachment"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="flex items-end gap-1 p-2">
          <input
            ref={input}
            type="file"
            multiple
            accept="image/*,video/*,audio/*,application/pdf"
            className="hidden"
            onChange={(e) => attach(e.target.files)}
          />
          {allowAttach ? (
            <IconButton label="Attach a photo, clip, sound or PDF" onClick={() => input.current?.click()} disabled={uploading}>
              {uploading ? <Spinner /> : <Paperclip className="size-5" />}
            </IconButton>
          ) : (
            <span className="w-2" />
          )}
          <label className="min-w-0 flex-1 py-2.5">
            <span className="sr-only">Message</span>
            <textarea
              ref={area}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={(e) => {
                const files = Array.from(e.clipboardData.files).filter(
                  (file) => /^(image|video|audio)\//.test(file.type) || file.type === "application/pdf",
                );
                if (!allowAttach || files.length === 0) return; // plain text pastes as usual
                e.preventDefault();
                void attach(files);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && window.matchMedia("(pointer: fine)").matches) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Message the studio assistant"
              className="block w-full resize-none bg-transparent text-[16px] leading-6 outline-none placeholder:text-mute/80"
            />
          </label>
          {working ? (
            <IconButton label="Stop" onClick={onStop} className="bg-raised text-paper">
              <Square className="size-4 fill-current" />
            </IconButton>
          ) : (
            <IconButton
              label="Send"
              onClick={send}
              disabled={uploading || (!text.trim() && attachments.length === 0)}
              className="bg-film text-signal-ink hover:bg-[#f7cf4f] hover:text-signal-ink disabled:bg-raised disabled:text-mute"
            >
              <ArrowUp className="size-5" />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
}
